import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import OutlookCallback from "./pages/OutlookCallback";
import OutlookOAuthSuccess from "./pages/OutlookOAuthSuccess";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeAdd from "./pages/EmployeeAdd";
import EmployeeEdit from "./pages/EmployeeEdit";
import Attendance from "./pages/Attendance";
import AttendanceReport from "./pages/AttendanceReport";
import Leave from "./pages/Leave";
import LeaveApproval from "./pages/LeaveApproval";
import LeaveAction from "./pages/LeaveAction";
import ExpenseAction from "./pages/ExpenseAction";
import Expenses from "./pages/Expenses";
import Departments from "./pages/Departments";
import Holidays from "./pages/Holidays";
import Profile from "./pages/Profile";
import ViewProfile from "./pages/ViewProfile";
import Settings from "./pages/Settings";
import Layout from "./layouts/MainLayout";
import SalarySlipList from "./modules/salary-slip/ListPage";
import SalarySlipCreate from "./modules/salary-slip/CreatePage";
import SalarySlipDetail from "./modules/salary-slip/DetailPage";
import SalarySlipEdit from "./modules/salary-slip/EditPage";
import SendSalarySlipPage from "./modules/payroll/SendSalarySlipPage";
import DashboardAnalytics from "./pages/DashboardAnalytics";
import PerformanceManagement from "./pages/PerformanceManagement";
import Documents from "./pages/Documents";
import DocumentUpload from "./pages/DocumentUpload";
import DocumentEdit from "./pages/DocumentEdit";
import Recruitment from "./pages/Recruitment";
import JobPostingCreate from "./pages/JobPostingCreate";
import JobPostingDetail from "./pages/JobPostingDetail";
import PayrollStatus from "./pages/PayrollStatus";
import RoleManagement from "./pages/RoleManagement";
import NavPermissionsManagement from "./pages/NavPermissionsManagement";
import Goals from "./pages/Goals";
import GoalCreate from "./pages/GoalCreate";
import GoalDetail from "./pages/GoalDetail";
import MyActivity from "./pages/MyActivity";
import TeamActivity from "./pages/TeamActivity";
import EmployeeReleaseOutlet from "./components/EmployeeReleaseOutlet";
import { getPostLoginPath } from "./config/featureFlags";
import { hasNavPath, hasNavWrite } from "./utils/navPermissions";

/**
 * navKey: the nav-item path this route belongs to (e.g. "/leave").
 *
 * Access decision priority:
 *  1. If user has explicit `navPermissions` array and navKey is set, navPermissions are source-of-truth.
 *  2. Otherwise, fall back to role-based access if route defines `roles`.
 *  3. Routes without navKey (profile) skip navPermissions check.
 */
const PrivateRoute = ({
  children,
  roles,
  navKey,
  requireWrite = false,
  requireExplicitNav = false,
}) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user?.role?.toUpperCase() || "EMPLOYEE";
  const isEmployeeOrHr = userRole === "EMPLOYEE" || userRole === "HR";

  // For specific modules, enforce explicit navPermissions assignment only
  // for EMPLOYEE/HR. ADMIN/MANAGER keep role-default access.
  if (requireExplicitNav && navKey) {
    if (isEmployeeOrHr) {
      if (!Array.isArray(user?.navPermissions) || !hasNavPath(user, navKey)) {
        return <Navigate to="/goals" replace />;
      }
      if (requireWrite && !hasNavWrite(user, navKey)) {
        return <Navigate to="/goals" replace />;
      }
      return children;
    }
  }

  // If custom nav permissions are explicitly set, they are the source of truth.
  // This keeps route access consistent with sidebar visibility.
  if (navKey && Array.isArray(user?.navPermissions)) {
    if (!hasNavPath(user, navKey)) {
      return <Navigate to={getPostLoginPath(user)} replace />;
    }
    if (requireWrite && !hasNavWrite(user, navKey)) {
      return <Navigate to={getPostLoginPath(user)} replace />;
    }
    return children;
  }

  // Fallback: role-based access when navPermissions are not explicitly set.
  if (roles && roles.length > 0) {
    const normalizedRoles = roles.map((r) => r.toUpperCase());
    if (!normalizedRoles.includes(userRole)) {
      return <Navigate to={getPostLoginPath(user)} replace />;
    }
  }
  if (requireWrite && navKey && !hasNavWrite(user, navKey)) {
    return <Navigate to={getPostLoginPath(user)} replace />;
  }

  return children;
};

function PostLoginRedirect() {
  const { user } = useAuth();
  return <Navigate to={getPostLoginPath(user)} replace />;
}

function AuthenticatedHomeRedirect() {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getPostLoginPath(user)} replace />;
}

function App() {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <PostLoginRedirect /> : <Login />}
        />

        <Route path="/auth/outlook/callback" element={<OutlookCallback />} />
        <Route path="/auth/outlook/success" element={<OutlookOAuthSuccess />} />

        <Route element={<Layout />}>
          <Route element={<EmployeeReleaseOutlet />}>
          <Route
            path="/dashboard"
            element={
              <PrivateRoute
                roles={["ADMIN", "MANAGER"]}
                navKey="/dashboard"
                requireExplicitNav
              >
                <Dashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/employees"
            element={
              <PrivateRoute
                roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]}
                navKey="/employees"
                requireExplicitNav
              >
                <Employees />
              </PrivateRoute>
            }
          />

          <Route
            path="/salary-slip"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/salary-slip">
                <SalarySlipList />
              </PrivateRoute>
            }
          />

          <Route
            path="/salary-slip/create"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/salary-slip">
                <SalarySlipCreate />
              </PrivateRoute>
            }
          />

          <Route
            path="/salary-slip/:id"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/salary-slip">
                <SalarySlipDetail />
              </PrivateRoute>
            }
          />

          <Route
            path="/salary-slip/:id/edit"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/salary-slip">
                <SalarySlipEdit />
              </PrivateRoute>
            }
          />

          <Route
            path="/payroll/send-salary-slip"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/payroll/send-salary-slip">
                <SendSalarySlipPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/payroll/status"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/payroll/status">
                <PayrollStatus />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/analytics"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/dashboard/analytics">
                <DashboardAnalytics />
              </PrivateRoute>
            }
          />

          <Route
            path="/performance"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/performance">
                <PerformanceManagement />
              </PrivateRoute>
            }
          />

          <Route
            path="/goals"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]} navKey="/goals">
                <Goals />
              </PrivateRoute>
            }
          />
          <Route
            path="/goals/create"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]} navKey="/goals">
                <GoalCreate />
              </PrivateRoute>
            }
          />
          <Route
            path="/goals/:id"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]} navKey="/goals">
                <GoalDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]} navKey="/activity">
                <MyActivity />
              </PrivateRoute>
            }
          />
          <Route
            path="/activity/team"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/activity/team">
                <TeamActivity />
              </PrivateRoute>
            }
          />

          <Route
            path="/documents"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/documents">
                <Documents />
              </PrivateRoute>
            }
          />

          <Route
            path="/documents/upload"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/documents">
                <DocumentUpload />
              </PrivateRoute>
            }
          />

          <Route
            path="/documents/:id/edit"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/documents">
                <DocumentEdit />
              </PrivateRoute>
            }
          />

          <Route
            path="/recruitment"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/recruitment">
                <Recruitment />
              </PrivateRoute>
            }
          />

          <Route
            path="/recruitment/jobs/create"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/recruitment">
                <JobPostingCreate />
              </PrivateRoute>
            }
          />

          <Route
            path="/recruitment/jobs/:id"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/recruitment">
                <JobPostingDetail />
              </PrivateRoute>
            }
          />

          <Route
            path="/employees/add"
            element={
              <PrivateRoute
                roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]}
                navKey="/employees"
                requireWrite
              >
                <EmployeeAdd />
              </PrivateRoute>
            }
          />

          <Route
            path="/employees/edit/:id"
            element={
              <PrivateRoute
                roles={["ADMIN", "MANAGER", "HR", "EMPLOYEE"]}
                navKey="/employees"
                requireWrite
              >
                <EmployeeEdit />
              </PrivateRoute>
            }
          />

          <Route
            path="/attendance"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/attendance">
                <Attendance />
              </PrivateRoute>
            }
          />

          <Route
            path="/attendance/report"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/attendance/report">
                <AttendanceReport />
              </PrivateRoute>
            }
          />

          <Route
            path="/leave"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/leave">
                <Leave />
              </PrivateRoute>
            }
          />

          <Route
            path="/expenses"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/expenses">
                <Expenses />
              </PrivateRoute>
            }
          />

          <Route
            path="/departments"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER"]} navKey="/departments">
                <Departments />
              </PrivateRoute>
            }
          />

          <Route
            path="/holidays"
            element={
              <PrivateRoute roles={["ADMIN", "MANAGER", "HR"]} navKey="/holidays">
                <Holidays />
              </PrivateRoute>
            }
          />

          {/* Profile routes — always accessible, no navKey restriction */}
          <Route
            path="/profile/view"
            element={
              <PrivateRoute>
                <ViewProfile />
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute roles={["ADMIN"]}>
                <Settings />
              </PrivateRoute>
            }
          />

          <Route
            path="/role-management"
            element={
              <PrivateRoute roles={["ADMIN"]} navKey="/role-management">
                <RoleManagement />
              </PrivateRoute>
            }
          />

          <Route
            path="/nav-permissions"
            element={
              <PrivateRoute roles={["ADMIN"]} navKey="/nav-permissions">
                <NavPermissionsManagement />
              </PrivateRoute>
            }
          />
          </Route>
        </Route>

        {/* Public route for email-based leave approval */}
        <Route path="/leave/approve" element={<LeaveApproval />} />

        {/* Public route for leave action with login redirect */}
        <Route path="/leave-action" element={<LeaveAction />} />

        {/* Public route for expense action with login redirect */}
        <Route path="/expense-action" element={<ExpenseAction />} />

        <Route path="/" element={<AuthenticatedHomeRedirect />} />
        <Route path="*" element={<AuthenticatedHomeRedirect />} />
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default App;
